import { FunctionComponent, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import backArrow from "../../assets/task/back_arrow.svg";
import Loader from "../../components/common/Loader";
import { ENTITY_FIELD_STATUS_REPORT } from "../../api/query";

type FilterType = "all" | "client" | "caregiver" | "employee" | "prospect";

const AgencyIssues: FunctionComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const agencyData = location.state?.agency;
    const teamMembers: any[] = location.state?.teamMembers || [];

    const [activeFilter, setActiveFilter] = useState<FilterType>("all");
    const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
    const [activeField, setActiveField] = useState<string | null>(null);

    const { data, loading } = useQuery(ENTITY_FIELD_STATUS_REPORT, {
        variables: { agency_id: parseInt(agencyData?.id), has_issue: true },
        fetchPolicy: "no-cache",
        skip: !agencyData?.id,
    });

    const allRecords: any[] = data?.entity_field_status_report?.records || [];

    // Records after member + entity-type filter (used for field tabs)
    const memberAndTypeFiltered = allRecords.filter(r => {
        const matchesMember = selectedMemberId !== null ? Number(r.member_id) === selectedMemberId : true;
        const matchesType = activeFilter === "all" ? true : r.entity_type === activeFilter;
        return matchesMember && matchesType;
    });

    // Unique field names from the current member+type filtered set
    const fieldTabs = Array.from(new Set(memberAndTypeFiltered.map(r => r.field_name))).filter(Boolean);

    // Reset activeField when filter changes
    const handleFilterChange = (f: FilterType) => {
        setActiveFilter(f);
        setActiveField(null);
    };
    const handleMemberChange = (id: number | null) => {
        setSelectedMemberId(id);
        setActiveField(null);
    };

    // Final visible records
    const visible = memberAndTypeFiltered.filter(r =>
        activeField ? r.field_name === activeField : true
    );

    const countFor = (f: FilterType) => {
        const base = selectedMemberId !== null ? allRecords.filter(r => Number(r.member_id) === selectedMemberId) : allRecords;
        return f === "all" ? base.length : base.filter(r => r.entity_type === f).length;
    };

    const fieldCount = (fieldName: string) =>
        memberAndTypeFiltered.filter(r => r.field_name === fieldName).length;

    const memberIssueCount = (memberId: number) =>
        allRecords.filter(r => Number(r.member_id) === memberId).length;

    const entityTypeColor = (t: string) => {
        if (t === "client") return "bg-purple-100 text-purple-700";
        if (t === "caregiver") return "bg-teal-100 text-teal-700";
        if (t === "prospect") return "bg-amber-100 text-amber-700";
        return "bg-indigo-100 text-indigo-700";
    };

    const entityName = (r: any) =>
        [r.entity_last_name, [r.entity_first_name, r.entity_middle_name].filter(Boolean).join(" ")].filter(Boolean).join(", ");

    const memberName = (r: any) =>
        [r.member_first_name, r.member_last_name].filter(Boolean).join(" ") || `Member ${r.member_id}`;

    const tabs: { key: FilterType; label: string }[] = [
        { key: "all", label: "All" },
        { key: "client", label: "Clients" },
        { key: "caregiver", label: "Caregivers" },
        { key: "employee", label: "Employees" },
        { key: "prospect", label: "Prospects" },
    ];

    return (
        <div className="w-full relative overflow-hidden flex flex-col items-start justify-start min-w-[360px] max-w-[100%] leading-[normal] tracking-[normal] text-left text-xl text-colors-text-black font-body-xs-semi-bold">
            <div className="self-stretch bg-gray overflow-hidden flex flex-col items-center justify-start relative min-h-[913px] max-w-full">
                <div className="w-full flex flex-col items-start justify-start py-40 px-20 mq1490:px-[30px] mq575:px-5 box-border gap-[24px] max-w-[100%]">

                    {/* Header */}
                    <div className="self-stretch flex flex-row items-center gap-3">
                        <img
                            className="w-6 h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
                            alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <div>
                            <h3 className="m-0 text-inherit font-bold font-inherit dark:text-white">
                                Issues
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-300 m-0">
                                {agencyData?.agency_name || "Agency"} · {allRecords.length} total
                            </p>
                        </div>
                    </div>

                    {/* Team Members Filter */}
                    <div className="w-full bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                        <h4 className="text-base font-bold text-gray-900 dark:text-white mb-4">Team Members</h4>
                        {loading ? <Loader /> : (
                        <div className="flex gap-4 flex-wrap">
                            <div
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                                    selectedMemberId === null
                                        ? "bg-blue-50 border-blue-500 dark:bg-blue-900/30"
                                        : "bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600 hover:border-gray-300"
                                }`}
                                onClick={() => handleMemberChange(null)}
                            >
                                <div className="w-10 h-10 rounded-full bg-blue-400 text-white text-sm font-semibold flex items-center justify-center">All</div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white m-0">All Members</p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 m-0">{allRecords.length} issues</p>
                                </div>
                            </div>
                            {teamMembers.map(member => (
                                <div
                                    key={member.id}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                                        selectedMemberId === member.id
                                            ? "bg-blue-50 border-blue-500 dark:bg-blue-900/30"
                                            : "bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600 hover:border-gray-300"
                                    }`}
                                    onClick={() => handleMemberChange(member.id)}
                                >
                                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center">
                                        {member.initials}
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white m-0">{member.name}</p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400 m-0">{memberIssueCount(member.id)} issues</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        )}
                    </div>

                    {/* Filter Tabs + List */}
                    <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                        <div className="flex border-b border-gray-200 dark:border-gray-700 gap-2">
                            {tabs.map(({ key, label }) => (
                                <button
                                    key={key}
                                    className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                                        activeFilter === key
                                            ? "bg-blue-600 blueboder-active text-white"
                                            : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    }`}
                                    onClick={() => handleFilterChange(key)}
                                >
                                    {label}
                                    {countFor(key) > 0 && (
                                        <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${
                                            activeFilter === key ? "bg-white/20 text-white" : "bg-orange-100 text-orange-600"
                                        }`}>
                                            {countFor(key)}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Field name tabs */}
                        {fieldTabs.length > 0 && (
                            <div className="flex gap-2 flex-wrap px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                                <button
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                        activeField === null
                                            ? "bg-blue-600 text-white blueboder-active"
                                            : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-blue-400"
                                    }`}
                                    onClick={() => setActiveField(null)}
                                >
                                    All Fields ({memberAndTypeFiltered.length})
                                </button>
                                {fieldTabs.map(field => (
                                    <button
                                        key={field}
                                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                            activeField === field
                                                ? "bg-blue-600 text-white"
                                                : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-blue-400"
                                        }`}
                                        onClick={() => setActiveField(field)}
                                    >
                                        {field} ({fieldCount(field)})
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="p-6 flex flex-col gap-4">
                            {loading ? (
                                <div className="flex justify-center py-12"><Loader /></div>
                            ) : visible.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 text-center">
                                    <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-gray-500 dark:text-gray-400 text-sm">No issues found</p>
                                </div>
                            ) : visible.map((r, idx) => (
                                <div key={idx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-5 flex flex-col gap-3">
                                    {/* Entity + type badges */}
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-xs font-semibold px-2 py-1 rounded-full capitalize ${entityTypeColor(r.entity_type)}`}>
                                                {r.entity_type}
                                            </span>
                                            <span className="text-sm font-bold text-gray-900 dark:text-white">{entityName(r)}</span>
                                        </div>
                                        <span className="text-xs font-semibold px-3 py-1 rounded-full border bg-orange-100 text-orange-700 border-orange-300">
                                            Issue
                                        </span>
                                    </div>
                                    {/* UUIDs */}
                                    <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                                        <span>Old UUID: <span className="font-mono text-gray-700 dark:text-gray-300">{r.entity_old_uuid || "-"}</span></span>
                                        <span>New UUID: <span className="font-mono text-gray-700 dark:text-gray-300">{r.entity_new_uuid || "-"}</span></span>
                                    </div>
                                    {/* Remarks */}
                                    {r.remarks && (
                                        <textarea
                                            readOnly
                                            className="w-full text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 rounded px-4 py-3 border border-gray-200 dark:border-gray-600 resize-y"
                                            value={r.remarks}
                                            rows={Math.min(r.remarks.split("\n").length + 1, 6)}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgencyIssues;
